import { useApolloClient } from "@apollo/client";
import {
  Box,
  Center,
  Flex,
  Icon,
  LinkBox,
  LinkOverlay,
  ListItem,
  Spinner,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { FragmentType, getFragmentData, gql } from "__generated__/gql";
import Filename from "components/Shared/Filename";
import { MouseEventHandler } from "react";
import { MdLink } from "react-icons/md";
import { ADMIN_CONTEXT } from "utils/apollo/hooks/admin";
import { assertPresence } from "utils/assertions";
import { asyncVoid } from "utils/asyncVoidHandler";
import { useGetTransparency } from "utils/colorHooks";
import useSignedUrlDownload from "utils/files/useSignedUrlDownload";
import { formatFilesize } from "utils/formatting";

const fragment = gql(/* GraphQL */ `
  fragment EmailAttachment on ReceivedEmailMessageAttachment {
    id
    originalFilename
    fileSize
    fileUnsignedUrl
    isLinkedDownload
  }
`);

const signedUrlQuery = gql(/* GraphQL */ `
  query DownloadEmailAttachment($id: UUID!, $attachmentFilename: String) {
    receivedEmailMessageAttachment(id: $id) {
      id
      fileUrl(attachmentFilename: $attachmentFilename)
    }
  }
`);

type Props = {
  attachment: FragmentType<typeof fragment>;
};

export default function EmailAttachment({
  attachment: attachmentFragment,
}: Props) {
  const client = useApolloClient();
  const attachment = getFragmentData(fragment, attachmentFragment);
  const getTransparency = useGetTransparency();

  const [performDownload, { isLoading }] = useSignedUrlDownload(
    async (documentId, filename) => {
      const { data } = await client.query({
        context: ADMIN_CONTEXT,
        fetchPolicy: "no-cache",
        query: signedUrlQuery,
        variables: { id: documentId, attachmentFilename: filename },
      });
      assertPresence(
        data.receivedEmailMessageAttachment?.fileUrl,
        "Could not get signed URL from response!",
      );
      return data.receivedEmailMessageAttachment.fileUrl;
    },
  );

  const handleClick: MouseEventHandler<HTMLAnchorElement> = (evt) => {
    evt.preventDefault();
    asyncVoid(performDownload(attachment.id, attachment.originalFilename));
  };

  return (
    <LinkBox
      as={ListItem}
      p={2}
      border="1px"
      borderColor="gray.200"
      borderRadius="md"
      bg="gray.50"
      aria-disabled={isLoading || undefined}
      position="relative"
    >
      <Flex alignItems="baseline" gap={2}>
        <LinkOverlay
          href={attachment.fileUnsignedUrl || ""}
          onClick={handleClick}
        >
          <Text fontSize="sm" fontWeight="medium">
            <Filename
              as="span"
              filename={attachment.originalFilename}
              color="gray.700"
              lineHeight="normal"
            />
          </Text>
        </LinkOverlay>

        <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">
          {formatFilesize(attachment.fileSize)}
        </Text>

        {attachment.isLinkedDownload && (
          <Tooltip
            label="File was downloaded from a link in the email"
            hasArrow
            placement="top"
            openDelay={300}
          >
            <Box as="span" pointerEvents="all" cursor="help" mt="-.5em">
              <Icon
                as={MdLink}
                fontSize="1.2em"
                color="magenta.dark"
                position="relative"
                top=".2em"
              />
            </Box>
          </Tooltip>
        )}
      </Flex>

      {isLoading && (
        <Center
          position="absolute"
          top={0}
          right={0}
          px={4}
          height="100%"
          backgroundColor={getTransparency("gray.50", 0.75)}
          borderRadius="md"
        >
          <Spinner size="sm" color="gray.500" />
        </Center>
      )}
    </LinkBox>
  );
}
